import React, { useState } from 'react';
import './App.css';
import GameBoard from './components/GameBoard';
import DataLoader from './components/DataLoader';
import { TrendPairData } from './types';

/**
 * App: Main application entry point
 * 
 * This is the root component of our React application that:
 * 1. Orchestrates data loading and application state
 * 2. Manages loading and error states
 * 3. Provides the main layout structure
 * 4. Renders appropriate child components based on state
 * 
 * It demonstrates several fundamental React concepts:
 * - Component composition (header, footer, main content)
 * - State management with useState
 * - Conditional rendering based on state
 * - Component communication via props and callbacks
 * - Inline styling (though in larger apps, you'd use CSS modules)
 */
const App: React.FC = () => {
  /**
   * Application State
   * ---
   * These state variables control what's displayed to the user
   */
  
  // Holds the trend data once loaded
  const [trendPairs, setTrendPairs] = useState<TrendPairData[]>([]);

  // Tracks whether data is currently being loaded
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Event Handlers
   * ---
   * Functions to respond to events from child components
   */
  
  /**
   * Called when trend data has finished loading
   * Updates state to store the loaded data and turn off loading indicator
   */
  const handleDataLoaded = (data: TrendPairData[]) => {
    setTrendPairs(data);
    setIsLoading(false);
  };

  /**
   * Component Rendering
   * ---
   * The JSX structure of our application with conditional logic
   */
  return (
    <div className="App">
      {/* Application Header */}
      <header>
        <h1 style={{
          fontSize: '48px',
          color: '#666',
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Zeitbomb 💣
        </h1>
        <p style={{ 
          fontSize: '18px', 
          color: '#888',
          marginBottom: '20px'
        }}>
          Can you guess which search term is more popular?
        </p>
      </header>
      
      {/* Data Loading Component (invisible) */}
      <DataLoader onDataLoaded={handleDataLoaded} />
      
      {/* Main Content Area - Conditionally renders based on state */}
      <main>
        {/* Loading State */}
        {isLoading && (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            color: '#666' 
          }}>
            Loading trend data...
          </div>
        )}
        
        {/* Successful Data Load - Show Game */}
        {!isLoading && trendPairs.length > 0 && (
          <GameBoard trendPairs={trendPairs} />
        )}
        
        {/* Error State - Data Failed to Load */}
        {!isLoading && trendPairs.length === 0 && (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            color: '#f44336' 
          }}>
            Could not load trend data. Please try refreshing the page.
          </div>
        )}
      </main>
      
      {/* Application Footer */}
      <footer style={{ 
        marginTop: '40px', 
        padding: '20px', 
        fontSize: '14px', 
        color: '#999',
        borderTop: '1px solid #eee'
      }}>
        <p>
          Trend data sourced from Google Trends. Updated daily.
        </p>
      </footer>
    </div>
  );
};

export default App;